import { useState, useMemo } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import LinkCard from '@/components/LinkCard'
import AddLinkModal from '@/components/AddLinkModal'
import NewsCard from '@/components/NewsCard'
import EntityDirectoryCard, { type EntityDirectoryVendor } from '@/components/EntityDirectoryCard'

interface Link {
  id: string
  name: string
  url: string
  description: string | null
  created_by: string
}

interface NewsArticle {
  id: string
  title: string
  description: string | null
  author: string
  publish_date: string | null
  image_url: string | null
  article_file_url: string | null
  is_displayed: boolean
  expiry_days: number | null
}

function isNewsArticleExpired(article: NewsArticle): boolean {
  if (!article.expiry_days || article.expiry_days <= 0) return false
  const baseDate = article.publish_date ? new Date(article.publish_date) : null
  if (!baseDate) return false
  const expiryDate = new Date(baseDate)
  expiryDate.setDate(expiryDate.getDate() + article.expiry_days)
  return expiryDate < new Date()
}

export default function Resources() {
  const { user } = useAuth()
  const [showAddModal, setShowAddModal] = useState(false)
  const [showAllArticles, setShowAllArticles] = useState(false)

  // Fetch user's links
  const { data: myLinks } = useQuery({
    queryKey: ['my-links', user?.id],
    queryFn: async () => {
      if (!user) return []
      try {
        const { data, error } = await supabase
          .from('links')
          .select(`
            id,
            name,
            url,
            description,
            created_by
          `)
          .eq('created_by', user.id)
          .order('created_at', { ascending: false })

        if (error) {
          console.warn('Error fetching my links:', error)
          return []
        }
        return (data || []) as Link[]
      } catch (err) {
        console.warn('Error in my links query:', err)
        return []
      }
    },
    enabled: !!user,
    retry: false,
  })

  // Fetch all community links (excluding user's links)
  const { data: communityLinks } = useQuery({
    queryKey: ['community-links', user?.id],
    queryFn: async () => {
      if (!user) return []
      try {
        const { data, error } = await supabase
          .from('links')
          .select(`
            id,
            name,
            url,
            description,
            created_by
          `)
          .neq('created_by', user.id)
          .order('created_at', { ascending: false })

        if (error) {
          console.warn('Error fetching community links:', error)
          return []
        }
        return (data || []) as Link[]
      } catch (err) {
        console.warn('Error in community links query:', err)
        return []
      }
    },
    enabled: !!user,
    retry: false,
  })

  // Directory-eligible broadcasters (opted in + contact email present)
  const { data: directoryBroadcasters } = useQuery({
    queryKey: ['directory-broadcasters'],
    queryFn: async () => {
      try {
        const { data, error } = await supabase
          .from('vendors')
          .select('id, user_id, vendor_name, vendor_description, contact_name, vendor_email, vendor_phone, website_url, vendor_logo_url, vendor_type')
          .eq('vendor_type', 'broadcaster')
          .eq('directory_opted_in', true)
          .not('vendor_email', 'is', null)
          .order('vendor_name', { ascending: true })
        if (error) {
          console.warn('Error fetching directory broadcasters:', error)
          return []
        }
        return (data || []).filter((v) => v.vendor_email && v.vendor_email.trim() !== '').map((v) => ({ ...v, vendor_type: 'broadcaster' as const })) as EntityDirectoryVendor[]
      } catch (err) {
        console.warn('Error in directory broadcasters query:', err)
        return []
      }
    },
    retry: false,
  })

  // Directory-eligible AD creators
  const { data: directoryAdCreators } = useQuery({
    queryKey: ['directory-ad-creators'],
    queryFn: async () => {
      try {
        const { data, error } = await supabase
          .from('vendors')
          .select('id, user_id, vendor_name, vendor_description, contact_name, vendor_email, vendor_phone, website_url, vendor_logo_url, vendor_type')
          .eq('vendor_type', 'ad-creator')
          .eq('directory_opted_in', true)
          .not('vendor_email', 'is', null)
          .order('vendor_name', { ascending: true })
        if (error) {
          console.warn('Error fetching directory AD creators:', error)
          return []
        }
        return (data || []).filter((v) => v.vendor_email && v.vendor_email.trim() !== '').map((v) => ({ ...v, vendor_type: 'ad-creator' as const })) as EntityDirectoryVendor[]
      } catch (err) {
        console.warn('Error in directory AD creators query:', err)
        return []
      }
    },
    retry: false,
  })

  // Fetch news articles (displayed only; expiry filtered when displaying unless "Show all" is checked)
  const { data: newsArticles = [] } = useQuery({
    queryKey: ['news-articles'],
    queryFn: async () => {
      try {
        const { data, error } = await supabase
          .from('news_articles')
          .select(`
            id,
            title,
            description,
            author,
            publish_date,
            image_url,
            article_file_url,
            is_displayed,
            expiry_days
          `)
          .eq('is_displayed', true)
          .order('publish_date', { ascending: false, nullsFirst: false })
          .order('created_at', { ascending: false })

        if (error) {
          console.warn('Error fetching news articles:', error)
          return []
        }
        return (data || []) as NewsArticle[]
      } catch (err) {
        console.warn('Error in news articles query:', err)
        return []
      }
    },
    retry: false,
  })

  const displayedNewsArticles = useMemo(() => {
    if (showAllArticles) return newsArticles
    return newsArticles.filter((article) => !isNewsArticleExpired(article))
  }, [newsArticles, showAllArticles])

  if (!user) {
    return (
      <div className="max-w-6xl mx-auto">
        <p style={{ color: 'var(--text-primary)' }}>
          Please log in to view links.
        </p>
      </div>
    )
  }

  return (
    <div className="max-w-6xl mx-auto">
      <h1 
        className="text-3xl font-bold mb-8"
        style={{ color: 'var(--text-primary)' }}
      >
        Resources
      </h1>

      {/* Latest News Section */}
      <section className="mb-12" aria-labelledby="latest-news-heading">
        <h2
          id="latest-news-heading"
          className="text-2xl font-bold mb-2"
          style={{ color: 'var(--text-primary)' }}
        >
          Latest News
        </h2>
        <label
          className="flex items-center gap-2 mb-6 cursor-pointer"
          style={{ color: 'var(--text-secondary)' }}
        >
          <input
            type="checkbox"
            checked={showAllArticles}
            onChange={(e) => setShowAllArticles(e.target.checked)}
            className="w-4 h-4"
            aria-describedby="show-all-articles-hint"
          />
          <span>Show all articles</span>
        </label>
        <p id="show-all-articles-hint" className="sr-only">
          When checked, expired articles are included in the list.
        </p>
        {displayedNewsArticles.length === 0 ? (
          <p style={{ color: 'var(--text-primary)' }}>
            No news articles available.
          </p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {displayedNewsArticles.map((article) => (
              <NewsCard key={article.id} article={article} />
            ))}
          </div>
        )}
      </section>

      {/* AD Creators directory */}
      <section className="mb-12" aria-labelledby="resources-ad-creators-heading">
        <h2
          id="resources-ad-creators-heading"
          className="text-2xl font-bold mb-2"
          style={{ color: 'var(--text-primary)' }}
        >
          AD Creators
        </h2>
        <p className="text-sm mb-6" style={{ color: 'var(--text-secondary)' }}>
          Contact details for audio description creators who have chosen to appear in the directory.
        </p>
        {!directoryAdCreators || directoryAdCreators.length === 0 ? (
          <p style={{ color: 'var(--text-primary)' }}>
            No AD creators in the directory yet.
          </p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {directoryAdCreators.map((entity) => (
              <EntityDirectoryCard key={entity.id} entity={entity} />
            ))}
          </div>
        )}
      </section>

      {/* Broadcasters directory */}
      <section className="mb-12" aria-labelledby="resources-broadcasters-heading">
        <h2
          id="resources-broadcasters-heading"
          className="text-2xl font-bold mb-2"
          style={{ color: 'var(--text-primary)' }}
        >
          Broadcasters
        </h2>
        <p className="text-sm mb-6" style={{ color: 'var(--text-secondary)' }}>
          Contact details for broadcasters who have chosen to appear in the directory.
        </p>
        {!directoryBroadcasters || directoryBroadcasters.length === 0 ? (
          <p style={{ color: 'var(--text-primary)' }}>
            No broadcasters in the directory yet.
          </p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {directoryBroadcasters.map((entity) => (
              <EntityDirectoryCard key={entity.id} entity={entity} />
            ))}
          </div>
        )}
      </section>

      {/* Links Section */}
      <section>
        <div className="flex items-center justify-between mb-6">
          <h2 
            className="text-2xl font-bold"
            style={{ color: 'var(--text-primary)' }}
          >
            Links
          </h2>
          <button
            onClick={() => setShowAddModal(true)}
            className="px-6 py-2 font-semibold rounded"
            style={{
              backgroundColor: 'var(--bg-interactive)',
              color: 'var(--text-inverse)',
            }}
          >
            Add Link
          </button>
        </div>

        {/* My Links Subsection */}
        <div className="mb-8">
          <h3 
            className="text-xl font-bold mb-4"
            style={{ color: 'var(--text-primary)' }}
          >
            My Links
          </h3>
          {!myLinks || myLinks.length === 0 ? (
            <p style={{ color: 'var(--text-primary)' }}>
              No links created yet.
            </p>
          ) : (
            <div className="space-y-4">
              {myLinks.map((link) => (
                <LinkCard key={link.id} link={link} />
              ))}
            </div>
          )}
        </div>

        {/* Community Links Subsection */}
        <div>
          <h3 
            className="text-xl font-bold mb-4"
            style={{ color: 'var(--text-primary)' }}
          >
            Community Links
          </h3>
          {!communityLinks || communityLinks.length === 0 ? (
            <p style={{ color: 'var(--text-primary)' }}>
              No community links available.
            </p>
          ) : (
            <div className="space-y-4">
              {communityLinks.map((link) => (
                <LinkCard key={link.id} link={link} />
              ))}
            </div>
          )}
        </div>
      </section>

      {showAddModal && (
        <AddLinkModal
          onClose={() => setShowAddModal(false)}
          onSuccess={() => {
            setShowAddModal(false)
            // Query will refetch automatically
          }}
        />
      )}
    </div>
  )
}

